import React, { useEffect, useState, useMemo } from "react";
import cloneDeep from "lodash.clonedeep";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Breadcrumb, Button, ButtonGroup, Checkbox, Col, Glyphicon, Modal, Row, Table } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import { useEventContext } from "../../../context";
import locale from "../../../locale";
import DeleteConfirmOverlay from "../../UI/DeleteConfirmOverlay";
import ProductModal from "./ProductModal";
import { addShopProduct, deleteShopProduct, getShopProducts, reorderShopProducts, updateShopProduct } from "../../../../api/cp/shop";
import { Event } from "../../Comet";

const newProduct = {
    titleRu: 'Новый приз',
    titleEn: '',
    descriptionRu: '',
    descriptionEn: '',
    price: 20,
    totalLimit: 100,
    limitPerPerson: 1,
    totalSold: 0,
    active: true,
    inStock: true,
}

export default () => {
    const { event } = useEventContext();

    const [products, setProducts] = useState([]);
    const [editingProduct, setEditingProduct] = useState(null);
    const [isSaving, setSaving] = useState(false);

    const getProducts = async () => {
        try {
            const _products = await getShopProducts(event.eventKey);
            setProducts(_products);
        } catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        getProducts();
    }, []);

    const handleAddProduct = async () => {
        setSaving(true);
        try {
            const product = await addShopProduct(event.eventKey, newProduct);
            setProducts(products => [...products, product])
            setEditingProduct(product);
        } catch (e) {
            console.error(e)
        } finally {
            setSaving(false);
        }
    }

    const handleSaveProduct = async (productID, data) => {
        setSaving(true);
        try {
            const product = await updateShopProduct(productID, data);
            setProducts(products => products.map(prod => prod.id === productID ? product : prod));
        } catch (e) {
            console.error(e)
        } finally {
            setSaving(false);
        }

    }

    const handleDeleteProduct = async (productID) => {
        try {
            await deleteShopProduct(productID);
            setProducts(products => products.filter(({ id }) => id !== productID))
        } catch (e) {
            console.error(e)
        }
    }

    const handleUpdateProducts = (product) => {
        setProducts(products => {
            const _products = cloneDeep(products);
            const index = _products.findIndex(_product => _product.id === product.id);
            if (index < 0) {
                _products.push(product)
            } else {
                _products[index] = { ..._products[index], ...product }
            }
            return _products;
        })
    }


    const handleDragEnd = async (result) => {
        if (!result.destination) {
            return;
        }

        let _products = cloneDeep(products);
        const _productsCopy = cloneDeep(products);
        const [removed] = _products.splice(result.source.index, 1);
        _products.splice(result.destination.index, 0, removed);

        _products = _products.map((product, sortOrder) => ({ ...product, sortOrder }));

        setProducts(_products);

        try {
            await reorderShopProducts(event.eventKey, { sortOrder: _products.map(({ id }) => id) });
        } catch (e) {
            console.error(e);
            setProducts(_productsCopy)
        }
    }

    const sortedProducts = useMemo(() => products.sort((a, b) => a.sortOrder - b.sortOrder), [products])

    return (
        <>
            <Event event={`create:product`} handler={(product) => handleUpdateProducts(product)} />
            <Event event={`update:product`} handler={(product) => handleUpdateProducts(product)} />
            <Event event={`delete:product`} handler={(product) => setProducts(products => products.filter(({ id }) => id !== product.id))} />
            <Breadcrumb>
                <LinkContainer to={`/event/${event.eventPassword}`}>
                    <Breadcrumb.Item>{locale('mainMenu.interactive')}</Breadcrumb.Item>
                </LinkContainer>
                <Breadcrumb.Item active>{locale('mainMenu.shop')}</Breadcrumb.Item>
            </Breadcrumb>
            <Row>
                <Col xs={12}>
                    <Button onClick={handleAddProduct} bsStyle="success">Добавить</Button>
                </Col>
            </Row>

            <Table>
                <thead>
                    <tr>
                        <th width={40}>ID</th>
                        <th>Название</th>
                        <th width={100}>Активен</th>
                        <th width={100}>В наличии</th>
                        <th width={100}>Стоимость</th>
                        <th width={100}>Лимит</th>
                        <th width={100}>Лимит на 1 участника</th>
                        <th width={100}>Остаток</th>
                        <th width={100}></th>
                    </tr>
                </thead>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <tbody
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {sortedProducts.map((product, index) => (
                                    <Draggable
                                        key={product.id}
                                        draggableId={String(product.id)}
                                        index={index}
                                    >
                                        {(provided) => (
                                            <tr ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <td>{product.id}</td>
                                                <td>{product.titleRu || product.titleEn}</td>
                                                <td>
                                                    <Checkbox
                                                        checked={product.active}
                                                        onChange={(e) => handleSaveProduct(product.id, { active: e.target.checked })}
                                                        inline
                                                        bsClass=""
                                                    />
                                                </td>
                                                <td>
                                                    <Checkbox
                                                        checked={product.inStock}
                                                        onChange={(e) => handleSaveProduct(product.id, { inStock: e.target.checked })}
                                                        inline
                                                        bsClass=""
                                                    />
                                                </td>
                                                <td>{product.price}</td>
                                                <td>{product.totalLimit}</td>
                                                <td>{product.limitPerPerson}</td>
                                                <td>{product.totalLimit - product.totalSold}</td>
                                                <td>
                                                    <ButtonGroup>
                                                        <Button bsSize="small" onClick={() => setEditingProduct(product)}><Glyphicon glyph="pencil" /></Button>
                                                        <DeleteConfirmOverlay title="Точно удалить приз?" buttonText="Удалить" onConfirm={() => handleDeleteProduct(product.id)}>
                                                            <Button bsSize="small" bsStyle="danger"><Glyphicon glyph="trash" /></Button>
                                                        </DeleteConfirmOverlay>
                                                    </ButtonGroup>
                                                </td>
                                            </tr>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </tbody>
                        )}
                    </Droppable>
                </DragDropContext>
            </Table>

            <Modal show={!!editingProduct} onHide={() => setEditingProduct(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Настройка приза
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ProductModal
                        id={editingProduct?.id}
                        product={editingProduct}
                        onEditProperty={(key, value) => setEditingProduct(product => ({ ...product, [key]: value }))}
                        onSave={(data) => handleSaveProduct(editingProduct?.id, data)}
                        isSaving={isSaving}
                    />
                </Modal.Body>
            </Modal>
        </>
    );
}

